
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CompanyPerks } from '@/lib/types';

export const useFetchCompanyPerks = (companyName: string) => {
  const [perks, setPerks] = useState<CompanyPerks | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerks = async () => {
      if (!companyName) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // First, find the company by name to get the ID
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('id')
          .eq('name', companyName)
          .maybeSingle();
          
        if (companyError) {
          throw companyError;
        }
        
        // If no company was found, stop here
        if (!companyData) {
          setPerks(null);
          setIsLoading(false);
          return;
        }
        
        // Now fetch perks for this company
        const { data: perksData, error: perksError } = await supabase
          .from('company_perks')
          .select('*')
          .eq('company_id', companyData.id)
          .maybeSingle();
          
        if (perksError) {
          throw perksError;
        }
        
        if (perksData) {
          const formattedPerks: CompanyPerks = {
            id: perksData.id,
            companyId: perksData.company_id,
            perk1: perksData.perk1 || undefined,
            perk2: perksData.perk2 || undefined,
            perk3: perksData.perk3 || undefined,
            perk4: perksData.perk4 || undefined,
            perk5: perksData.perk5 || undefined,
            createdAt: new Date(perksData.created_at).getTime()
          };
          
          setPerks(formattedPerks);
        } else {
          setPerks(null);
        }
      } catch (err: any) {
        console.error('Error fetching company perks:', err);
        setError(err.message);
        setPerks(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPerks();
  }, [companyName]);
  
  return { perks, isLoading, error };
};
